/* =====================================================================================
   Org structure.

   Deliberately NOT the same thing as the employee list. A position exists whether or not
   anyone holds it, and that is the point: an org chart showing only filled seats hides the
   two facts that matter most to a company this size, namely what is missing and who is
   carrying more than one hat.

   Four kinds of unit, and the distinction is legal as much as organisational:

     governance   the Board. Directors, not employees.
     executive    the people running the company day to day
     function     Product, Commercial, and whatever comes next
     advisory     the Scientific Advisory Board and the Patient Advocate. Paid a fee rather
                  than a salary, and every payment is a transfer of value that is
                  disclosable under ABPI, EFPIA or Open Payments depending on where the
                  person sits. Filing them under "staff" would lose that.

   These live in the `app` schema, not `hr`, precisely so an org chart can be shown without
   opening the walled personnel data. A vacant Head of Sales is not confidential; a salary is.
   ===================================================================================== */

import { BACKEND } from './db';

export interface OrgPosition {
  id: string;
  key: string;
  title: string;
  unit: string;
  unit_id: string;
  unit_type: 'governance' | 'executive' | 'function' | 'advisory' | string;
  reports_to_id: string | null;
  status: 'filled' | 'vacant' | 'planned' | string;
  seniority: string | null;
  engagement: string | null;
  headcount: number;
  remit: string | null;
  depth: number;
  path: string;
  held_by: string | null;
  holders: number;
  sort_order: number;
}

export interface OrgUnit {
  id: string; key: string; name: string; unit_type: string;
  purpose: string | null; sort_order: number; active: boolean;
}

const live = () => BACKEND === 'supabase';

async function orgId(): Promise<string> {
  const { supabase } = await import('./supabase');
  const { data } = await supabase().schema('app').from('orgs').select('id').limit(1).maybeSingle();
  if (!data?.id) throw new Error('No organisation visible to this member.');
  return data.id;
}

export async function fetchOrgChart(): Promise<OrgPosition[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').from('v_org_chart').select('*');
  if (error) throw new Error(error.message);
  // Sorted by unit then seniority so the Board reads before the executive, and a reader
  // sees governance before operations rather than an alphabetical jumble.
  const rank: Record<string, number> = { governance: 0, executive: 1, function: 2, advisory: 3 };
  return (data ?? []).sort((a: OrgPosition, b: OrgPosition) =>
    (rank[a.unit_type] ?? 9) - (rank[b.unit_type] ?? 9)
    || a.unit.localeCompare(b.unit)
    || a.depth - b.depth
    || a.sort_order - b.sort_order);
}

export async function fetchUnits(): Promise<OrgUnit[]> {
  if (!live()) return [];
  const { supabase } = await import('./supabase');
  const { data, error } = await supabase().schema('app').from('org_units')
    .select('*').eq('active', true).order('sort_order');
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function addUnit(u: {
  key: string; name: string; unitType: string; purpose?: string;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('org_units').insert({
    org_id: await orgId(), key: u.key, name: u.name,
    unit_type: u.unitType, purpose: u.purpose || null, active: true,
  });
  if (error) throw new Error(error.code === '23505' ? `Unit "${u.key}" already exists.` : error.message);
}

export async function addPosition(p: {
  unitId: string; key: string; title: string; remit?: string;
  seniority?: string; engagement?: string; headcount?: number; reportsToId?: string | null;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('positions').insert({
    org_id: await orgId(), unit_id: p.unitId, key: p.key, title: p.title,
    remit: p.remit || null, seniority: p.seniority || 'senior',
    engagement: p.engagement || 'employee', headcount: p.headcount ?? 1,
    reports_to_id: p.reportsToId || null,
    // A new seat starts vacant. Marking it filled is a separate act, because "we have a Head
    // of Sales" and "we have written down that we need one" are very different statements.
    status: 'vacant',
  });
  if (error) throw new Error(error.code === '23505' ? `Position "${p.key}" already exists.` : error.message);
}

export async function updatePosition(id: string, patch: Record<string, unknown>) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('positions').update(patch).eq('id', id);
  if (error) throw new Error(error.message);
}

/**
 * Put someone in a seat. `holderName` covers advisors and directors who have no employee
 * record and should not have one: a non-executive is not on the payroll, and creating an
 * employment record for them would be wrong in a way an auditor would notice.
 */
export async function assignHolder(a: {
  positionId: string; employeeId?: string | null; holderName?: string;
  startedOn?: string; isInterim?: boolean;
}) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('position_holders').insert({
    org_id: await orgId(), position_id: a.positionId,
    employee_id: a.employeeId || null,
    holder_name: a.holderName || null,
    started_on: a.startedOn || new Date().toISOString().slice(0, 10),
    is_interim: a.isInterim ?? false,
  });
  if (error) throw new Error(error.message);
  await updatePosition(a.positionId, { status: 'filled' });
}

/** End a tenure rather than delete it: who held a seat and when is part of the record. */
export async function endTenure(holderId: string, positionId: string, stillFilled: boolean) {
  const { supabase } = await import('./supabase');
  const { error } = await supabase().schema('app').from('position_holders')
    .update({ ended_on: new Date().toISOString().slice(0, 10) }).eq('id', holderId);
  if (error) throw new Error(error.message);
  if (!stillFilled) await updatePosition(positionId, { status: 'vacant' });
}

export const UNIT_TYPES = ['governance', 'executive', 'function', 'advisory'] as const;
export const SENIORITY = ['board', 'executive', 'senior', 'mid', 'junior', 'advisor'] as const;
export const ENGAGEMENT = ['employee', 'director', 'non_executive', 'advisor', 'contractor'] as const;
